import type { ServiceAuditTrailEntry } from "shared";

export interface AuditLineSegment {
  text: string;
  /** Rendered in `lightBg.high`; the rest sits in `lightBg.low`. */
  emphasised: boolean;
}

/** Regex-escape, so a name like "O'Brien (IT)" can't compile as a pattern. */
const escapeRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * The words in a sentence that carry the meaning: who did it, who it was done
 * to, and the values that changed.
 *
 * Pulled off the **entry**, not parsed out of the sentence — the catalog builds
 * its lines from exactly these fields, so this is the same data rather than a
 * guess at it. That is what keeps the catalog (and its 184 tests) untouched
 * while still letting the UI two-tone the text.
 */
export const collectEmphasisTokens = (
  entry: ServiceAuditTrailEntry,
): string[] => {
  const tokens = [entry.activity_by?.name];
  for (const detail of entry.audit_details ?? []) {
    tokens.push(
      detail.activity_for?.name,
      // `detailLabel` reads this too, so a label sourced from it must be
      // emphasisable — otherwise a KB article title would render in the plain
      // shade while every other label renders dark, and the two-tone split
      // would drift from the text the catalog actually built.
      detail.meta_data?.line_item?.name,
      detail.previous_state,
      detail.current_state,
    );
  }
  return tokens
    .filter((t): t is string => typeof t === "string" && t.trim() !== "")
    .map((t) => t.trim());
};

/**
 * Splits a sentence into emphasised / plain runs so the row can render names
 * and changed values darker than the connective words around them —
 * "**John Doe** assigned **Jane Doe** to the ticket".
 *
 * Tokens are matched longest-first so that "John Doe" wins over a bare "John",
 * which would otherwise leave a stray "  Doe" in the plain shade.
 *
 * Returns a single plain segment when nothing matches, so the caller never has
 * to special-case an empty result.
 */
export const splitAuditLine = (
  line: string,
  tokens: string[],
): AuditLineSegment[] => {
  const unique = [...new Set(tokens)]
    .filter(Boolean)
    .sort((a, b) => b.length - a.length);

  if (unique.length === 0 || line === "") {
    return [{ text: line, emphasised: false }];
  }

  // Word boundaries, but only where they make sense: `\b` is defined against
  // word characters, so wrapping a token that starts or ends with punctuation
  // ("(IT)", "jeremy@email.com") in `\b` would stop it matching at all. Each
  // token gets the guard only on the sides where its own edge is a word char.
  const bounded = unique.map((token) => {
    const escaped = escapeRegExp(token);
    const left = /^\w/.test(token) ? "\\b" : "";
    const right = /\w$/.test(token) ? "\\b" : "";
    return `${left}${escaped}${right}`;
  });
  const pattern = new RegExp(`(${bounded.join("|")})`, "g");
  const segments: AuditLineSegment[] = [];
  let cursor = 0;

  for (const match of line.matchAll(pattern)) {
    const start = match.index;
    if (start > cursor) {
      segments.push({ text: line.slice(cursor, start), emphasised: false });
    }
    segments.push({ text: match[0], emphasised: true });
    cursor = start + match[0].length;
  }

  if (cursor < line.length) {
    segments.push({ text: line.slice(cursor), emphasised: false });
  }

  return segments;
};
